import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import FileInput from "@/components/ui/file-input";
import { uploadAdminFiles } from "@/lib/files.api";
import { Tickets, Close } from "@/components/icons";
import { clientSupportTicketsApi } from "../../services/client-support-tickets.api";
import styles from "./NewClientSupportTicketModal.module.scss";

interface NewClientSupportTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Verilirse talep bu eğitime bağlı oluşturulur (read-only chip görünür). */
  educationId?: string;
  /** Read-only chip'te göstermek için eğitim adı (educationId ile beraber gönderilmeli). */
  educationName?: string;
}

const NewClientSupportTicketModal: React.FC<
  NewClientSupportTicketModalProps
> = ({ isOpen, onClose, onSuccess, educationId, educationName }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetState = () => {
    setTitle("");
    setDescription("");
    setFiles([]);
    setError(null);
  };

  const handleClose = () => {
    if (loading) return;
    resetState();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;

    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();

    if (!trimmedTitle || !trimmedDescription) {
      setError(
        t("clientSupportTickets.newTicket.validation"),
      );
      return;
    }

    setLoading(true);
    setError(null);
    try {
      let fileIds: string[] | undefined;
      if (files.length > 0) {
        const uploaded = await uploadAdminFiles(files);
        fileIds = uploaded.map((f) => f.id);
      }

      await clientSupportTicketsApi.create({
        title: trimmedTitle,
        description: trimmedDescription,
        fileIds,
        educationId,
      });

      resetState();
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Error creating support ticket:", err);
      setError(
        t("clientSupportTickets.newTicket.error"),
      );
    } finally {
      setLoading(false);
    }
  };

  const customHeader = (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        <span className={styles.headerIcon}>
          <Tickets width={20} height={20} color="#E7212B" />
        </span>
        <h3 className={styles.headerTitle}>
          {t("clientSupportTickets.newTicket.title")}
        </h3>
      </div>
      <button
        type="button"
        className={styles.headerCloseBtn}
        onClick={handleClose}
        aria-label={t("common.close")}
      >
        <Close width={20} height={20} color="#505766" />
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      customHeader={customHeader}
      size="lg"
      withForm
      formId="newSupportTicketForm"
      handleSubmit={handleSubmit}
      submitButtonText={t("clientSupportTickets.newTicket.submit")}
      loading={loading}
    >
      <form
        id="newSupportTicketForm"
        onSubmit={handleSubmit}
        className={styles.form}
      >
        {educationId && educationName && (
          <div className={styles.fieldGroup}>
            <label className={styles.educationLockedLabel}>
              {t("clientSupportTickets.newTicket.fields.education")}
            </label>
            <div className={styles.educationLockedChip}>
              <Tickets width={16} height={16} color="#505766" />
              <span>{educationName}</span>
            </div>
          </div>
        )}

        <div className={styles.fieldGroup}>
          <Input
            label={t("clientSupportTickets.newTicket.fields.title")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder={t("clientSupportTickets.newTicket.fields.titlePlaceholder")}
          />
        </div>

        <div className={styles.fieldGroup}>
          <Textarea
            label={t("clientSupportTickets.newTicket.fields.description")}
            value={description}
            maxLength={5000}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            placeholder={t("clientSupportTickets.newTicket.fields.descriptionPlaceholder")}
          />
        </div>

        <div className={styles.fieldGroup}>
          <FileInput
            label={t("clientSupportTickets.newTicket.fields.file")}
            buttonLabel={t("clientSupportTickets.newTicket.fields.fileButton")}
            placeholder={t("clientSupportTickets.newTicket.fields.filePlaceholder")}
            maxSize={500}
            supportedFormats={["PNG", "JPG", "JPEG", "PDF", "DOC", "DOCX"]}
            onFileSelect={(selectedFiles: File[]) => setFiles(selectedFiles)}
            onFileRemove={(index) =>
              setFiles((prev) => prev.filter((_, i) => i !== index))
            }
          />
        </div>

        {error && <div className={styles.errorText}>{error}</div>}
      </form>
    </Modal>
  );
};

export default NewClientSupportTicketModal;
